import React, { FC } from 'react';

import { MarketingCard } from 'components/features/components/marketing-card';
import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import { IMarketingCard, IMarketingCardFragment } from 'generated/sanity-graphql';
import { filterContentCardsByServiceMode } from 'state/braze/hooks/utils';
import { ContentCardTypes } from 'state/braze/types';
import { selectors, useAppSelector } from 'state/global-state';
import { useServiceModeLive } from 'state/service-mode';

import { BrazeMarketingCard } from '../braze-marketing-card';

import { Container } from './styled';
import { IMarketingCardGroupProps } from './types';
import { hasRules, tileShouldBeDisplayed } from './utils';

/**
 *
 * MarketingCardGroup renders a group of marketing cards in a grid
 *
 */
export const MarketingCardGroup: FC<React.PropsWithChildren<IMarketingCardGroupProps>> = ({
  doc,
  onShowTermsModal,
}) => {
  const { isHomePageFeedEnabled, isNewsFeedEnabled } = useContentCardsNotificationsExperiment();
  const brazeContentCards = useAppSelector(selectors.loyalty.selectHomePageFeedContentCards);
  const serviceMode = useServiceModeLive();

  const cards = doc.Cards as IMarketingCard[];

  if (!cards || cards.length <= 0) {
    return null;
  }

  const brazeContentCardsList = isHomePageFeedEnabled ? brazeContentCards : [];
  const visibleBrazeContentCardsList = filterContentCardsByServiceMode(
    brazeContentCardsList,
    serviceMode
  );
  const topVisibleBrazeContentCardsList = visibleBrazeContentCardsList
    .filter(card => card.type === ContentCardTypes.CAPTIONED)
    .slice(0, 6);

  // NOTE: Return all cards, except those cards that have rule and do not meet the requirement
  const sanityCardsList = cards.filter(card => {
    if (hasRules(card) && !tileShouldBeDisplayed(card)) {
      return false;
    }
    return true;
  });

  // Sanity cards only when Braze homepage feed is off (no fallback when Braze is on but empty)
  const cardsList = !isNewsFeedEnabled && !isHomePageFeedEnabled ? sanityCardsList : [];

  if (topVisibleBrazeContentCardsList.length === 0 && cardsList.length === 0) {
    return null;
  }

  return (
    <Container testID="marketing-tile-group-grid">
      {topVisibleBrazeContentCardsList.map((card, index) => {
        const key = `BrazeMarketingCard_${card.id}`;
        return (
          <BrazeMarketingCard
            key={key}
            card={card}
            onShowTermsModal={onShowTermsModal}
            index={index}
          />
        );
      })}

      {cardsList.map((card, index) => {
        if (!card) {
          return null;
        }
        const key = `MarketingCard_${card._id}`;
        return (
          <MarketingCard
            key={key}
            card={card as IMarketingCardFragment}
            onShowTermsModal={onShowTermsModal}
            index={index}
          />
        );
      })}
    </Container>
  );
};
